import React, { useEffect, useId, useRef } from "react";
import { ScaleLoader } from "react-spinners";
import AccessibleDialog from "../UI/Modals/AccessibleDialog/AccessibleDialog";
import OrderItemPicker from "./OrderItemPicker";
import OrderItemsList from "./OrderItemsList";
import DeliverySection from "./DeliverySection";
import {
  estimatedItemsSubtotal,
  useConsultOrderContext,
} from "../../contexts/ConsultOrderContext";
import {
  draftDeliveryFee,
  ConsultOrderValidationErrors,
} from "../../utils/consultOrderPayload";
import { isRfzoItemInStock } from "../../utils/consultRfzoPayload";
import { CONSULT_LOYALTY_PAYMENT_ENABLED } from "../../utils/consultOrder.constants";
import {
  ConsultOrderPhase,
  ConsultSuccessKind,
} from "../../hooks/useConsultOrderLifecycle";
import { CnCOrderResponse } from "../../types/api";
import { ConsultRfzoTherapyResponse } from "../../types/consultRfzo.types";
import styles from "./ConsultOrderFormModal.module.scss";

// Fields in top-to-bottom visual order, matched to their `data-consult-field`
// markers. A failed submit focuses (and scrolls to) the FIRST field in this order
// that has a validation error, so the pharmacist is taken straight to the problem.
const FIELD_FOCUS_ORDER: (keyof ConsultOrderValidationErrors)[] = [
  "items",
  "address",
  "delivery",
  "deliveryTimeSlot",
  "personalId",
  "firstName",
  "phoneNumber",
];

const focusFirstInvalidField = (
  errors: ConsultOrderValidationErrors,
  container: HTMLElement | null
): void => {
  const scope: ParentNode = container ?? document;
  for (const key of FIELD_FOCUS_ORDER) {
    if (!errors[key]) {
      continue;
    }
    const el = scope.querySelector<HTMLElement>(
      `[data-consult-field="${key}"]`
    );
    if (el) {
      el.focus();
      if (typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      return;
    }
  }
};

// ITAM-2583: the consult order form. It is purely presentational over the shared
// ConsultOrderContext (draft + prefill) — item/delivery editing lives in the
// sibling sections; this shell composes them, renders customer fields + an
// ESTIMATED review, and wires the lifecycle callbacks (submit/close/success).
// The 201 response is authoritative, so every total here is labelled "procenjeno".

export interface ConsultOrderFormModalProps {
  isOpen: boolean;
  phase: ConsultOrderPhase;
  graceActive: boolean;
  consultEnded: boolean;
  createdOrder: CnCOrderResponse | null;
  submitError: string | null;
  validationErrors: ConsultOrderValidationErrors;
  /** Bumped on each failed submit attempt; drives focus to the first invalid field. */
  submitAttemptId: number;
  lineErrors: Record<number, string>;
  /**
   * ITAM-2614: "RFZO_ORDER" renders the therapy's read-only item list instead
   * of the picker and a READ-ONLY customer section (D11 — step 2 silently
   * discards identity edits); the delivery section stays live.
   */
  mode?: "CNC" | "RFZO_ORDER";
  /** The created therapy (item list + identity snapshot) for RFZO order mode. */
  rfzoResult?: ConsultRfzoTherapyResponse | null;
  /** Which submit produced SUCCESS — keys the success copy (D7: RFZO has no order id). */
  successKind?: ConsultSuccessKind | null;
  onSubmit: () => void;
  onClose: () => void;
  onCloseSuccess: () => void;
  onDismissError: () => void;
}

const TITLE_ID = "consult-order-form-title";

const formatAmount = (value: number): string =>
  `${value.toLocaleString("sr-RS", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} RSD`;

const ConsultOrderFormModal: React.FC<ConsultOrderFormModalProps> = ({
  isOpen,
  phase,
  graceActive,
  consultEnded,
  createdOrder,
  submitError,
  validationErrors,
  submitAttemptId,
  lineErrors,
  mode = "CNC",
  rfzoResult = null,
  successKind = null,
  onSubmit,
  onClose,
  onCloseSuccess,
  onDismissError,
}) => {
  const { prefill, draft, updateDraft } = useConsultOrderContext();

  const isRfzoOrder = mode === "RFZO_ORDER";

  const closeRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const reactId = useId();
  const firstNameId = `${reactId}-first-name`;
  const secondNameId = `${reactId}-second-name`;
  const phoneId = `${reactId}-phone`;
  const noteId = `${reactId}-note`;
  const firstNameErrId = `${firstNameId}-error`;
  const phoneErrId = `${phoneId}-error`;

  const callerName =
    [prefill?.user.firstName, prefill?.user.secondName]
      .filter((part) => part && part.trim())
      .join(" ")
      .trim() ||
    prefill?.user.username ||
    "korisnika";

  const isSuccess = phase === "SUCCESS";
  const isSubmitting = phase === "SUBMITTING";

  // The button stays clickable so a click always RUNS validation and surfaces the
  // field errors (it is only inert while a submit is actually in flight). The
  // lifecycle submit() still validates before any POST — an invalid draft never
  // reaches the network.
  const submitDisabled = isSubmitting;

  // On a failed submit attempt (nonce bump), move focus to the first invalid field.
  useEffect(() => {
    if (submitAttemptId <= 0) {
      return;
    }
    focusFirstInvalidField(validationErrors, bodyRef.current);
    // Intentionally keyed to the attempt nonce only: re-focus once per attempt,
    // never while the pharmacist is editing (validationErrors changes with it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAttemptId]);

  // react-modal applies initialFocusRef only via onAfterOpen (the open
  // transition). The FORM/SUBMITTING -> SUCCESS swap happens with the modal
  // already open — the focused submit button unmounts — so move focus to the
  // success close button here or keyboard/screen-reader users land on <body>.
  useEffect(() => {
    if (isSuccess) {
      closeRef.current?.focus();
    }
  }, [isSuccess]);

  const isPickup = draft.fulfillment === "PICKUP";
  // A loyalty-paid delivery fee is settled in points, not dinars — keep it out
  // of the RSD summary entirely (no fee row, excluded from the total). With the
  // loyalty switch off (ITAM-2756/2757) this is always false, so every delivery
  // shows its fee row and carries it into the estimated total.
  const paidWithPoints =
    CONSULT_LOYALTY_PAYMENT_ENABLED &&
    !isPickup &&
    draft.paymentType === "LOYALTY_POINTS";
  // RFZO order mode: the participation total comes from the backend response —
  // it EXCLUDES understocked items (D9), so a self-computed line sum would lie.
  const subtotal = isRfzoOrder
    ? rfzoResult?.totalParticipationPrice ?? 0
    : estimatedItemsSubtotal(draft.items);
  const deliveryFee = paidWithPoints ? 0 : draftDeliveryFee(draft);
  const estimatedTotal = subtotal + deliveryFee;

  const handleRequestClose = () => {
    if (isSuccess) {
      onCloseSuccess();
    } else {
      onClose();
    }
  };

  return (
    <AccessibleDialog
      isOpen={isOpen}
      onRequestClose={handleRequestClose}
      titleId={TITLE_ID}
      initialFocusRef={isSuccess ? closeRef : undefined}
      maxWidth={760}
      className={styles.dialogInner}
    >
      <div className={styles.container}>
        <header className={styles.header}>
          <h2 id={TITLE_ID} className={styles.title}>
            {isRfzoOrder
              ? `RFZO rezervacija — ${callerName}`
              : `Nova rezervacija — ${callerName}`}
          </h2>
        </header>

        {isSuccess ? (
          <div className={styles.successBody} role="status">
            {successKind === "RFZO" ? (
              // D7: the RFZO order endpoint returns 200 with an empty body —
              // there is no order id/total to show.
              <p className={styles.successHeadline}>
                RFZO rezervacija je kreirana.
              </p>
            ) : createdOrder ? (
              <>
                <p className={styles.successHeadline}>
                  Rezervacija #{createdOrder.id} je kreirana.
                </p>
                <dl className={styles.successMeta}>
                  <div className={styles.successRow}>
                    <dt>Ukupno</dt>
                    <dd>
                      {createdOrder.totalPriceWithDelivery != null
                        ? formatAmount(createdOrder.totalPriceWithDelivery)
                        : "—"}
                    </dd>
                  </div>
                  <div className={styles.successRow}>
                    <dt>Broj artikala</dt>
                    <dd>{createdOrder.items?.length ?? draft.items.length}</dd>
                  </div>
                </dl>
              </>
            ) : (
              <p className={styles.successHeadline}>
                {submitError ?? "Rezervacija je već kreirana."}
              </p>
            )}
            <div className={styles.footerActions}>
              <button
                type="button"
                ref={closeRef}
                className={styles.primary}
                onClick={onCloseSuccess}
              >
                Zatvori
              </button>
            </div>
          </div>
        ) : (
          <>
            {(consultEnded || graceActive) && (
              <div className={styles.graceBanner} role="status">
                Konsultacija je završena — rezervaciju možete poslati još kratko.
              </div>
            )}
            {submitError && (
              <div className={styles.errorBanner} role="alert">
                <span className={styles.errorText}>{submitError}</span>
                <button
                  type="button"
                  className={styles.errorDismiss}
                  onClick={onDismissError}
                  aria-label="Zatvori poruku o grešci"
                >
                  Zatvori
                </button>
              </div>
            )}

            <div className={styles.body} ref={bodyRef}>
              <section className={styles.section} aria-label="Artikli">
                <h3 className={styles.sectionTitle}>Artikli</h3>
                {isRfzoOrder ? (
                  <>
                    <p className={styles.rfzoItemsNote}>
                      Artikli, količine i participacije su preuzeti iz kreirane
                      RFZO terapije i ne mogu da se menjaju.
                    </p>
                    <ul
                      className={styles.rfzoItemList}
                      aria-label="Artikli terapije"
                    >
                      {(rfzoResult?.items ?? []).map((it, idx) => (
                        <li
                          key={it.itemResponse?.id ?? idx}
                          className={styles.rfzoItemRow}
                        >
                          <span className={styles.rfzoItemName}>
                            {it.itemResponse?.name ?? "Artikal"}
                            {!isRfzoItemInStock(it) && (
                              <span className={styles.rfzoStockBadge}>
                                nije na stanju
                              </span>
                            )}
                          </span>
                          <span className={styles.rfzoItemMeta}>
                            {/* Per-unit participation (the line total lives in
                                rfzoParticipationPrice and is summed into the
                                response's totalParticipationPrice). */}
                            {it.rfzoQuantity ?? "—"} kom · participacija{" "}
                            {it.rfzoUnitParticipationPrice != null
                              ? formatAmount(it.rfzoUnitParticipationPrice)
                              : "—"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <>
                    <OrderItemPicker />
                    <OrderItemsList lineErrors={lineErrors} />
                  </>
                )}
              </section>

              <section
                className={styles.section}
                aria-label="Način preuzimanja"
              >
                <h3 className={styles.sectionTitle}>
                  Način preuzimanja
                </h3>
                <DeliverySection errors={validationErrors} />
              </section>

              <section className={styles.section} aria-label="Podaci korisnika">
                <h3 className={styles.sectionTitle}>Podaci korisnika</h3>
                {isRfzoOrder ? (
                  // D11: the order's customer identity is the STEP-1 snapshot —
                  // edits here would be silently discarded by the backend, so
                  // the section renders read-only from the created therapy.
                  <>
                    <p className={styles.rfzoItemsNote}>
                      Podaci su preuzeti iz kreirane terapije.
                    </p>
                    <dl className={styles.rfzoCustomer}>
                      <div className={styles.rfzoCustomerRow}>
                        <dt>Ime</dt>
                        <dd>{rfzoResult?.firstName || "—"}</dd>
                      </div>
                      <div className={styles.rfzoCustomerRow}>
                        <dt>Prezime</dt>
                        <dd>{rfzoResult?.secondName || "—"}</dd>
                      </div>
                      <div className={styles.rfzoCustomerRow}>
                        <dt>Broj telefona</dt>
                        <dd>{rfzoResult?.userPhone || "—"}</dd>
                      </div>
                    </dl>
                  </>
                ) : (
                <div className={styles.fieldGrid}>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor={firstNameId}>
                      Ime
                    </label>
                    <input
                      id={firstNameId}
                      className={styles.input}
                      data-consult-field="firstName"
                      type="text"
                      value={draft.firstName}
                      onChange={(e) =>
                        updateDraft({ firstName: e.target.value })
                      }
                      aria-invalid={validationErrors.firstName ? true : undefined}
                      aria-describedby={
                        validationErrors.firstName ? firstNameErrId : undefined
                      }
                    />
                    {validationErrors.firstName && (
                      <span
                        id={firstNameErrId}
                        className={styles.fieldError}
                        role="alert"
                      >
                        {validationErrors.firstName}
                      </span>
                    )}
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor={secondNameId}>
                      Prezime
                    </label>
                    <input
                      id={secondNameId}
                      className={styles.input}
                      type="text"
                      value={draft.secondName}
                      onChange={(e) =>
                        updateDraft({ secondName: e.target.value })
                      }
                    />
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor={phoneId}>
                      Broj telefona
                    </label>
                    <input
                      id={phoneId}
                      className={styles.input}
                      data-consult-field="phoneNumber"
                      type="tel"
                      value={draft.phoneNumber}
                      onChange={(e) =>
                        updateDraft({ phoneNumber: e.target.value })
                      }
                      aria-invalid={
                        validationErrors.phoneNumber ? true : undefined
                      }
                      aria-describedby={
                        validationErrors.phoneNumber ? phoneErrId : undefined
                      }
                    />
                    {validationErrors.phoneNumber && (
                      <span
                        id={phoneErrId}
                        className={styles.fieldError}
                        role="alert"
                      >
                        {validationErrors.phoneNumber}
                      </span>
                    )}
                  </div>

                  <div className={`${styles.field} ${styles.fieldFull}`}>
                    <label className={styles.label} htmlFor={noteId}>
                      Napomena
                    </label>
                    <textarea
                      id={noteId}
                      className={styles.textarea}
                      rows={2}
                      value={draft.note}
                      onChange={(e) => updateDraft({ note: e.target.value })}
                    />
                  </div>
                </div>
                )}
              </section>

              <section className={styles.section} aria-label="Pregled">
                <h3 className={styles.sectionTitle}>Pregled</h3>
                <dl className={styles.summary}>
                  <div className={styles.summaryRow}>
                    <dt>
                      {isRfzoOrder
                        ? "Participacija (iz terapije)"
                        : "Artikli (procenjeno)"}
                    </dt>
                    <dd>{formatAmount(subtotal)}</dd>
                  </div>
                  {!isPickup && !paidWithPoints && (
                    <div className={styles.summaryRow}>
                      <dt>Usluga rezervacije (orijentaciono)</dt>
                      <dd>{formatAmount(deliveryFee)}</dd>
                    </div>
                  )}
                  <div
                    className={`${styles.summaryRow} ${styles.summaryTotal}`}
                  >
                    <dt>Ukupno (procenjeno)</dt>
                    <dd>{formatAmount(estimatedTotal)}</dd>
                  </div>
                </dl>
                <p className={styles.summaryNote}>
                  {isRfzoOrder
                    ? "Participacija je preuzeta iz kreirane terapije (bez artikala koji nisu na stanju); iznos usluge rezervacije je orijentacioni."
                    : "Iznosi su orijentacioni — konačnu cenu potvrđuje kreirana rezervacija."}
                </p>
              </section>
            </div>

            <footer className={styles.footer}>
              <button
                type="button"
                className={styles.secondary}
                onClick={onClose}
              >
                Otkaži
              </button>
              <button
                type="button"
                className={styles.primary}
                onClick={onSubmit}
                disabled={submitDisabled}
              >
                {isSubmitting ? (
                  <span className={styles.submitSpinner}>
                    <ScaleLoader color="#ffffff" height={14} width={3} />
                  </span>
                ) : (
                  "Pošalji rezervaciju"
                )}
              </button>
            </footer>
          </>
        )}
      </div>
    </AccessibleDialog>
  );
};

export default ConsultOrderFormModal;
